import React, { useEffect, useReducer, useState } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { Radio, RadioGroup } from '@wartek-id/radio'
import { Text } from '@wartek-id/text'
import { useToast } from '@wartek-id/toast'
import { historyUtils } from '@wartek-id/toolbox-helper'

import {
  instrumentList,
  questionsByInstrument,
} from 'app/AssessmentPage/SurveyValidasiKompetensiGuru/data'
import { useMutationApi } from 'utils/api'
import { EmptyState } from 'components/EmptyState'

import type { IInstrumentQuestions } from 'app/AssessmentPage/SurveyValidasiKompetensiGuru/data'

export function usePostSurveyAnswers(opts) {
  return useMutationApi(`/survey/validasi-kompetensi-guru`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}

interface IinstrumentState {
  isLast: boolean
  isFirst: boolean
  instrument: string
  questions: IInstrumentQuestions[]
  validatorTarget: string[]
}

const generateValidatorTarget = (currentInstrument) => {
  const currentQuestionByInstrument = questionsByInstrument.find(
    (instrument) => currentInstrument === instrument.type
  )
  let targets = []
  currentQuestionByInstrument.questions.forEach((question) => {
    question.items.forEach((subQuestion) =>
      targets.push(`${currentInstrument}_${question.id}_${subQuestion.order}`)
    )
  })
  return targets
}

const defaultSurveyState: IinstrumentState = {
  isLast: false,
  isFirst: true,
  instrument: instrumentList[0],
  questions: questionsByInstrument[0].questions,
  validatorTarget: generateValidatorTarget(instrumentList[0]),
}

const surveyReducer = (state, action): IinstrumentState => {
  const currentIndex = instrumentList.findIndex(
    (item) => item === state.instrument
  )

  if (
    (action === 'prev' && currentIndex === 0) ||
    (action === 'next' && currentIndex === instrumentList.length - 1)
  ) {
    return state
  }

  if (action === 'reset') {
    return defaultSurveyState
  }

  const currentInstrumentItem = questionsByInstrument.find((instrument) => {
    return (
      instrumentList[
        action === 'prev' ? currentIndex - 1 : currentIndex + 1
      ] === instrument.type
    )
  })

  return {
    isLast: action === 'next' && currentIndex + 1 === instrumentList.length - 1,
    isFirst: action === 'prev' && currentIndex - 1 === 0,
    instrument:
      action === 'next'
        ? instrumentList[currentIndex + 1]
        : instrumentList[currentIndex - 1],
    questions: currentInstrumentItem.questions,
    validatorTarget: generateValidatorTarget(
      instrumentList[action === 'prev' ? currentIndex - 1 : currentIndex + 1]
    ),
  }
}

const SURVEY_AVAILABLE = process.env.NEXT_PUBLIC_SURVEY_KOMPETENSI_ENABLED

const SurveyValidasiKompetensiGuru = () => {
  const toast = useToast()
  const { back } = useRouter()
  const [surveyState, dispatch] = useReducer(surveyReducer, defaultSurveyState)
  const [answers, setAnswers] = useState([])
  const [isSent, setIsSent] = useState(false)
  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  })

  const { mutateAsync, isLoading } = usePostSurveyAnswers({
    onSuccess: () => {
      toast({
        message: 'Jawaban survey anda telah terkirim!',
        type: 'success',
      })
      setIsSent(true)
    },
    onError: () => {
      toast({
        message: 'Mohon maaf, jawaban survey gagal dikirim. Silahkan coba lagi',
      })
    },
  })

  const handleKuesionerAnswer = (questionName: string, value: string) => {
    setAnswers((old) => {
      return {
        ...old,
        [questionName]: value,
      }
    })
  }

  const scrollToError = React.useCallback(() => {
    const elements = Object.keys(errors)
      .map((name) => document.getElementsByName(name)[0])
      .filter((el) => !!el)
    elements.sort((a, b) => b.scrollHeight - a.scrollHeight)
    elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errors])

  useEffect(() => {
    if (Object.keys(errors).length !== 0) {
      scrollToError()
    }
  }, [errors, scrollToError])

  const validateStep = async () => {
    const isValidated = await trigger(surveyState.validatorTarget)
    if (isValidated) {
      if (surveyState.isLast) {
        await mutateAsync({ answers: getValues() })
      } else {
        dispatch('next')
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    } else {
      scrollToError()
    }
  }

  if (SURVEY_AVAILABLE !== 'true') {
    return (
      <EmptyState
        title="Fitur Tidak Tersedia"
        description="Maaf, fitur ini belum tersedia."
      />
    )
  }

  return (
    <>
      <Head>
        <title>Survey</title>
      </Head>
      <HeaderBar fixed className="z-10">
        <HeaderBarLeftAction
          icon={<Icon>arrow_back</Icon>}
          onClick={() => historyUtils.backToApp(back)}
        />
        <HeaderBarTitle>Survey Validasi Kompetensi</HeaderBarTitle>
      </HeaderBar>
      <div
        className="relative pb-5 z-0"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        {!isSent ? (
          <div className="p-4">
            <div>
              {surveyState.questions.map((question) => {
                return (
                  <div
                    key={`${surveyState.instrument}_${question.id}`}
                    className="flex mb-6"
                  >
                    <Text>
                      <strong>{question.id}</strong>.
                    </Text>
                    <div className="ml-2">
                      <Text variant="heading-sm" className="mb-2">
                        {question.text}
                      </Text>
                      {question.items.map((subQuestion) => (
                        <div
                          className="mb-4 last:mb-0"
                          key={`${surveyState.instrument}_${question.id}_${subQuestion.order}`}
                        >
                          <Text className="mb-1">{subQuestion.question}</Text>
                          <Text className="mb-2">
                            Setelah melihat deskripsi tugas di atas, menurut
                            Anda
                          </Text>
                          <RadioGroup
                            onChange={(value) => {
                              handleKuesionerAnswer(
                                `${surveyState.instrument}_${question.id}_${subQuestion.order}`,
                                value
                              )
                            }}
                            value={
                              answers[
                                `${surveyState.instrument}_${question.id}_${subQuestion.order}`
                              ]
                            }
                            name={`${surveyState.instrument}_${question.id}_${subQuestion.order}`}
                            style={{ flexDirection: 'column' }}
                          >
                            {subQuestion.questionScale.map((opt) => (
                              <Radio
                                key={`${surveyState.instrument}_${question.id}_${subQuestion.order}_${opt.value}`}
                                {...register(
                                  `${surveyState.instrument}_${question.id}_${subQuestion.order}`,
                                  {
                                    required: {
                                      value: true,
                                      message: 'Harus dipilih salah satu',
                                    },
                                  }
                                )}
                                className="mb-3"
                                value={opt.value.toString()}
                              >
                                <Text className="capitalize" as="span">
                                  {opt.label}
                                </Text>
                              </Radio>
                            ))}
                          </RadioGroup>
                          <Text variant="body-sm" color="critical">
                            {
                              errors[
                                `${surveyState.instrument}_${question.id}_${subQuestion.order}`
                              ]?.message
                            }
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center justify-between mt-4 -mx-2">
              <div className="px-2 w-1/2">
                {!surveyState.isFirst && (
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => {
                      dispatch('prev')
                    }}
                    color="blue"
                    size="lg"
                  >
                    Kembali
                  </Button>
                )}
              </div>
              <div className="px-2 w-1/2">
                <Button
                  isLoading={isLoading}
                  fullWidth
                  onClick={validateStep}
                  color="blue"
                  size="lg"
                >
                  {surveyState.isLast ? 'Kirim' : 'Berikutnya'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center p-4 min-h-screen">
            <div>
              <Text className="mb-4" variant="heading-md">
                Terima kasih atas partisipasi anda dalam mengisi survey
              </Text>
              <Button
                fullWidth
                onClick={() => {
                  dispatch('reset')
                  setIsSent(false)
                }}
              >
                Kembali
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

SurveyValidasiKompetensiGuru.layoutOpts = {
  hasHeader: true,
}

export default SurveyValidasiKompetensiGuru
